import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Collapse,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  inquiryApi,
  SupplierInquiry,
  UpdateInquiryRequest,
} from '@texas/api/endpoints/inquiryApi';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { CollapseButton } from '@texas/components/shared/CollapseButton';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { CurrencyRate } from '@texas/components/shared/CurrencyRate';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ReactSelectOption, ReactSelectOptionWithBadges } from '@texas/types';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { setValueAsDecimalExtender } from '../sales/shared';
import { useBlurRound } from './useBlurRound';

interface FormData {
  currencyId: number | null;
  exchangeRate: number;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  mpq: number | null;
  sampleLeadTime: number | null;
  bulkLeadTime: number | null;
  shipmentLeadTime: number | null;
  developmentCost: number | null;
  developmentRefundQuantity: number | null;
  developmentRefundTimeMonths: number | null;
  handlingFeeId: number | null;
  freightCostId: number | null;
  customsCodeId: number | null;
}

interface Props {
  inquiry: SupplierInquiry;
  onUpdated: () => void;
}

export function InquiryMetadataForm({ inquiry, onUpdated }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    useCurrenciesData: { findCurrency },
    branchesData: [, getBranches],
    handlingFeesData: [handlingFees, getHandlingFees],
    freightCostsData: [freightCosts, getFreightCosts],
    customsCodesData: [, getCustomsCodes],
    useCustomsCodesData: { customsCodesInBranch },
  } = useMetadata();

  const { control, register, handleSubmit, setValue, formState } =
    useForm<FormData>({
      defaultValues: {
        currencyId: inquiry.currencyId,
        exchangeRate: inquiry.exchangeRate,
        sampleCharge: inquiry.sampleCharge,
        mouldCharge: inquiry.mouldCharge,
        surcharge: inquiry.surcharge,
        mpq: inquiry.mpqQuantity ?? null,
        sampleLeadTime: inquiry.sampleLeadTime ?? null,
        bulkLeadTime: inquiry.bulkLeadTime ?? null,
        shipmentLeadTime: inquiry.shipmentLeadTime ?? null,
        developmentCost: inquiry.developmentCost ?? null,
        developmentRefundQuantity: inquiry.developmentRefundQuantity ?? null,
        developmentRefundTimeMonths:
          inquiry.developmentRefundTimeMonths ?? null,
        handlingFeeId: inquiry.handlingFeeId ?? null,
        freightCostId: inquiry.freightCostId ?? null,
        customsCodeId: inquiry.customsCodeId ?? null,
      },
      mode: 'onChange',
    });

  const { request: updateRequest, loading } = useApiRequest(
    inquiryApi.updateInquiry,
  );

  const developmentCostDisclosure = useDisclosure({
    defaultIsOpen:
      inquiry.developmentCost != null ||
      inquiry.developmentRefundQuantity != null ||
      inquiry.developmentRefundTimeMonths != null,
  });

  const currencyId = useWatch({ control, name: 'currencyId' });
  const exchangeRate = useWatch({ control, name: 'exchangeRate' });
  const developmentRefundTimeMonths = useWatch({
    control,
    name: 'developmentRefundTimeMonths',
  });

  const { data: mergedCurrencies, refetch: refetchMergedCurrencies } =
    useApiResource(suppliersApi.getSupplierCurrenciesForBranch);

  useEffect(() => {
    getBranches();
    getHandlingFees();
    getFreightCosts();
    getCustomsCodes();
    if (inquiry.supplierId != null) {
      refetchMergedCurrencies(inquiry.supplierId, inquiry.branchId);
    }
  }, [
    getBranches,
    getHandlingFees,
    getFreightCosts,
    getCustomsCodes,
    refetchMergedCurrencies,
    inquiry.supplierId,
    inquiry.branchId,
  ]);

  const selectedCurrency = mergedCurrencies?.find((c) => c.id === currencyId);
  const isDefaultCurrency = selectedCurrency?.isDefaultBranchCurrency ?? false;
  const currencyCode = currencyId ? findCurrency(currencyId)?.code : '';

  async function onSubmit(data: FormData) {
    const req: UpdateInquiryRequest = {
      currencyId: data.currencyId ?? inquiry.currencyId,
      exchangeRate: data.exchangeRate,
      sampleCharge: data.sampleCharge,
      mouldCharge: data.mouldCharge,
      surcharge: data.surcharge,
      mpq: data.mpq,
      sampleLeadTime: data.sampleLeadTime,
      bulkLeadTime: data.bulkLeadTime,
      shipmentLeadTime: data.shipmentLeadTime,
      developmentCost: data.developmentCost,
      developmentRefundQuantity: data.developmentRefundQuantity,
      developmentRefundTimeMonths: data.developmentRefundTimeMonths,
      supplierContactId: inquiry.supplierContactId,
      handlingFeeId: data.handlingFeeId,
      freightCostId: data.freightCostId,
      customsCodeId: data.customsCodeId,
    };

    const res = await updateRequest(inquiry.id, req);
    if (!res.hasError) {
      toast({
        title: t('general.saved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onUpdated();
    } else {
      toast({
        title: t('general.error'),
        description: res.error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  function handleRefreshRate(value: number) {
    setValue('exchangeRate', value);
  }

  const blurRound = useBlurRound();

  const currencyOptions =
    mergedCurrencies?.map<ReactSelectOptionWithBadges>((c) => ({
      label: `${c.code} (${c.branchBuyingRate})`,
      value: c.id,
      badge1: c.isSupplierCustom
        ? { visible: true, label: `Supplier: ${c.buyingRate}` }
        : undefined,
    })) ?? [];

  const handlingFeeOptions = handlingFees
    .filter((f) => !f.archived)
    .map<ReactSelectOption>((f) => ({
      label: `${f.name} (${f.value}%)`,
      value: f.id,
    }));

  const freightCostOptions = freightCosts
    .filter((f) => !f.archived)
    .map<ReactSelectOption>((f) => ({
      label: `${f.name} (${f.value}%)`,
      value: f.id,
    }));

  const customsCodeOptions = customsCodesInBranch(
    inquiry.branchId,
  ).map<ReactSelectOption>((code) => ({
    label: `${code.identifier} — ${code.name}`,
    value: code.id,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card variant="dark" mb={4}>
        <CardBody>
          <Flex gap={4} wrap="wrap" mb={4}>
            <TexasInputWrapper
              label={t('inquiry.currency')}
              formControlProps={{ width: 'fit-content' }}
            >
              <Controller
                name="currencyId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TexasSelect<ReactSelectOptionWithBadges>
                    showBadges={true}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                    isClearable={false}
                    value={
                      value != null && findCurrency(value)
                        ? {
                            label: `${findCurrency(value)!.code} (${
                              selectedCurrency?.branchBuyingRate
                            })`,
                            value: value,
                          }
                        : null
                    }
                    options={currencyOptions}
                    onChange={(e) => {
                      if (!e) return;
                      onChange(e.value);
                      const currency = mergedCurrencies?.find(
                        (c) => c.id === e.value,
                      );
                      if (currency) {
                        const rate = currency.isSupplierCustom
                          ? currency.buyingRate
                          : currency.branchBuyingRate;
                        setValue('exchangeRate', rate);
                      }
                    }}
                  />
                )}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.exchangeRate')}
              formControlProps={{ width: 'fit-content' }}
              error={formState.errors.exchangeRate}
              utilButtons={
                <Menu>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="ghost"
                    isDisabled={isDefaultCurrency || !selectedCurrency}
                  >
                    {t('variant.supplier.refreshRate')}
                  </MenuButton>
                  <MenuList>
                    {selectedCurrency?.isSupplierCustom && (
                      <MenuItem
                        onClick={() =>
                          handleRefreshRate(selectedCurrency.buyingRate)
                        }
                        fontSize="sm"
                        isDisabled={
                          exchangeRate === selectedCurrency.buyingRate
                        }
                      >
                        {t('variant.supplier.pullFromSupplier')} (
                        {selectedCurrency.buyingRate})
                      </MenuItem>
                    )}
                    {selectedCurrency && (
                      <MenuItem
                        onClick={() =>
                          handleRefreshRate(selectedCurrency.branchBuyingRate)
                        }
                        fontSize="sm"
                        isDisabled={
                          exchangeRate === selectedCurrency.branchBuyingRate
                        }
                      >
                        {t('variant.supplier.pullFromBranch')} (
                        {selectedCurrency.branchBuyingRate})
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              }
            >
              <Input
                variant="texas"
                isDisabled={isDefaultCurrency}
                {...register('exchangeRate', {
                  required: t('variant.supplier.exchangeRateRequired'),
                  validate: (value) =>
                    value > 0 ||
                    t('variant.supplier.exchangeRateGreaterThanZero'),
                  setValueAs: (v) => setValueAsDecimalExtender(v, false),
                })}
              />
            </TexasInputWrapper>
            {isDefaultCurrency && (
              <Flex align="flex-end">
                <Text fontSize="xs" color="gray.500">
                  {t('variant.supplier.branchDefaultCurrencyLocked')}
                </Text>
              </Flex>
            )}
          </Flex>
          {currencyId != null && (
            <CurrencyRate
              branchId={inquiry.branchId}
              currencyId={currencyId}
              exchangeRate={exchangeRate}
              exchangeRateType="purchase"
              supplierId={inquiry.supplierId ?? undefined}
            />
          )}
        </CardBody>
      </Card>
      <Card variant="dark" mb={4}>
        <CardBody>
          <Flex gap={4} wrap="wrap" mb={4}>
            <TexasInputWrapper
              label={t('inquiry.sampleCharge')}
              formControlProps={{ width: 'fit-content' }}
              rightAddon={currencyCode}
            >
              <Input
                variant="texas"
                type="number"
                {...register('sampleCharge', {
                  valueAsNumber: true,
                  onBlur: blurRound(inquiry.decimalsInPrice, (v) =>
                    setValue('sampleCharge', v),
                  ),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.mouldCharge')}
              formControlProps={{ width: 'fit-content' }}
              rightAddon={currencyCode}
            >
              <Input
                variant="texas"
                type="number"
                {...register('mouldCharge', {
                  valueAsNumber: true,
                  onBlur: blurRound(inquiry.decimalsInPrice, (v) =>
                    setValue('mouldCharge', v),
                  ),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.surcharge')}
              formControlProps={{ width: 'fit-content' }}
              rightAddon={currencyCode}
            >
              <Input
                variant="texas"
                type="number"
                {...register('surcharge', {
                  valueAsNumber: true,
                  onBlur: blurRound(inquiry.decimalsInPrice, (v) =>
                    setValue('surcharge', v),
                  ),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.mpq')}
              formControlProps={{ width: 'fit-content' }}
            >
              <Input
                variant="texas"
                type="number"
                placeholder="—"
                {...register('mpq', {
                  setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                  onBlur: blurRound(0, (v) => setValue('mpq', v)),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.sampleLeadTime')}
              formControlProps={{ width: 'fit-content' }}
              rightAddon={t('inquiry.days')}
            >
              <Input
                variant="texas"
                type="number"
                placeholder="—"
                {...register('sampleLeadTime', {
                  setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.bulkLeadTime')}
              formControlProps={{ width: 'fit-content' }}
              rightAddon={t('inquiry.days')}
            >
              <Input
                variant="texas"
                type="number"
                placeholder="—"
                {...register('bulkLeadTime', {
                  setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.shipmentLeadTime')}
              formControlProps={{ width: 'fit-content' }}
              rightAddon={t('inquiry.days')}
            >
              <Input
                variant="texas"
                type="number"
                min={0}
                placeholder="—"
                {...register('shipmentLeadTime', {
                  setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                })}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.handlingFee')}
              formControlProps={{ width: 'fit-content' }}
            >
              <Controller
                name="handlingFeeId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TexasSelect<ReactSelectOption>
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                    isClearable={true}
                    value={
                      value != null
                        ? handlingFeeOptions.find((o) => o.value === value) ??
                          null
                        : null
                    }
                    options={handlingFeeOptions}
                    onChange={(e) => onChange(e ? e.value : null)}
                  />
                )}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.freightCost')}
              formControlProps={{ width: 'fit-content' }}
            >
              <Controller
                name="freightCostId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TexasSelect<ReactSelectOption>
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                    isClearable={true}
                    value={
                      value != null
                        ? freightCostOptions.find((o) => o.value === value) ??
                          null
                        : null
                    }
                    options={freightCostOptions}
                    onChange={(e) => onChange(e ? e.value : null)}
                  />
                )}
              />
            </TexasInputWrapper>
            <TexasInputWrapper
              label={t('inquiry.customsCode')}
              formControlProps={{ width: 'fit-content' }}
            >
              <Controller
                name="customsCodeId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TexasSelect<ReactSelectOption>
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                    menuPlacement="top"
                    isClearable={true}
                    value={
                      value != null
                        ? customsCodeOptions.find((o) => o.value === value) ??
                          null
                        : null
                    }
                    options={customsCodeOptions}
                    onChange={(e) => onChange(e ? e.value : null)}
                  />
                )}
              />
            </TexasInputWrapper>
          </Flex>
          <CollapseButton
            type="button"
            width="100%"
            mb={2}
            variant="texas-light"
            isOpen={developmentCostDisclosure.isOpen}
            onToggle={developmentCostDisclosure.onToggle}
            label={t('inquiry.developmentCost')}
          />
          <Collapse
            in={developmentCostDisclosure.isOpen}
            animateOpacity={true}
            style={{ width: '100%' }}
          >
            <Flex gap={4} wrap="wrap" mb={4} pt={2}>
              <TexasInputWrapper
                label={t('inquiry.developmentCost')}
                formControlProps={{ width: 'fit-content' }}
                rightAddon={currencyCode}
              >
                <Input
                  variant="texas"
                  type="number"
                  min={0}
                  placeholder="—"
                  {...register('developmentCost', {
                    setValueAs: (v) => setValueAsDecimalExtender(v, true),
                    onBlur: blurRound(inquiry.decimalsInPrice, (v) =>
                      setValue('developmentCost', v),
                    ),
                  })}
                />
              </TexasInputWrapper>
              <TexasInputWrapper
                label={t('inquiry.developmentRefundQuantity')}
                formControlProps={{ width: 'fit-content' }}
              >
                <Input
                  variant="texas"
                  type="number"
                  min={0}
                  placeholder="—"
                  {...register('developmentRefundQuantity', {
                    setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                  })}
                />
              </TexasInputWrapper>
              <Flex direction="column" align="flex-start">
                <TexasInputWrapper
                  label={t('inquiry.developmentRefundTimeMonths')}
                  formControlProps={{ width: 'fit-content' }}
                  rightAddon={t('inquiry.months')}
                >
                  <Input
                    variant="texas"
                    type="number"
                    min={0}
                    placeholder="—"
                    {...register('developmentRefundTimeMonths', {
                      setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                    })}
                  />
                </TexasInputWrapper>
                <ButtonGroup isAttached={true} size="xs" mt={1} px={1}>
                  {[
                    { label: t('inquiry.unset'), value: null },
                    { label: `6 ${t('inquiry.months')}`, value: 6 },
                    { label: `12 ${t('inquiry.months')}`, value: 12 },
                  ].map((option) => (
                    <Button
                      key={option.value ?? 'unset'}
                      type="button"
                      variant={
                        developmentRefundTimeMonths === option.value
                          ? 'texas-solid'
                          : 'outline'
                      }
                      onClick={() =>
                        setValue('developmentRefundTimeMonths', option.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      {option.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Flex>
            </Flex>
          </Collapse>
          <Flex gap={2} justify="flex-end">
            <Button variant="texas-solid" isLoading={loading} type="submit">
              {t('general.save')}
            </Button>
          </Flex>
        </CardBody>
      </Card>
    </form>
  );
}
